import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';

/** Static so Tailwind's scanner can see the class — an arbitrary variant built
 *  by template literal compiles to nothing, because the scanner reads source
 *  text, not runtime values. Add an entry if a page ever needs another cut-off. */
const COLLAPSE_CLASS: Record<2 | 3, string> = {
  2: '[&>*:nth-child(n+3)]:max-md:hidden',
  3: '[&>*:nth-child(n+4)]:max-md:hidden',
};

/**
 * The one "Hoạt động" row (design 13 §5). A list row, NOT a timeline with a
 * vertical rule — the rule was ink spent on saying "these happened in order",
 * which a list already says.
 *
 * Icons are muted by default. Colour is reserved for money, exactly as the
 * attention system spends it: paid = brand, overdue = red. Nothing else gets a
 * colour, which is why an audit feed (no payment events) is entirely muted.
 *
 * Sizes are pinned in px because --spacing is 0.2rem: the design's 14.4px icon
 * and 9.6px rhythm have no spacing-utility equivalents.
 */
export function ActivityItem({
  icon: Icon,
  iconClassName,
  text,
  time,
  className,
}: {
  icon: LucideIcon;
  /** Money only — `text-brand-500` for paid, `text-destructive` for overdue. */
  iconClassName?: string;
  text: ReactNode;
  /** Already-formatted relative time ("2 ngày trước"). */
  time: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-[9.6px] border-b border-surface-100 py-[9.6px] last:border-b-0',
        className,
      )}
    >
      <Icon
        className={cn('mt-[3.2px] h-[14.4px] w-[14.4px] shrink-0', iconClassName ?? 'text-surface-500')}
        strokeWidth={1.8}
      />
      <span className="min-w-0 flex-1 text-sm text-surface-700">{text}</span>
      <span className="shrink-0 text-xs text-surface-400">{time}</span>
    </div>
  );
}

/**
 * The card frame the rows sit in, plus the phone collapse (13 §5: "Phone: hiện
 * 2–3 dòng đầu + Xem tất cả hoạt động").
 *
 * The collapse is CSS, not `useIsMobile()`: that hook is `false` on its first
 * render, so branching on it would paint the full list for one frame on a
 * phone. Rows past `collapseAfter` get `max-md:hidden` until the viewer expands
 * them, and the expand button is `md:hidden` — on desktop everything is visible
 * from first paint and the button never exists.
 *
 * `collapseAfter` is opt-in. Pass it where Hoạt động is a secondary section
 * under other content (the student page). Do NOT pass it where the viewer
 * navigated to the activity list on purpose (the Khách hàng tab) — collapsing
 * the thing someone just asked for is hostile.
 */
export function ActivityList({
  children,
  count,
  collapseAfter,
  emptyLabel,
  footer,
}: {
  children: ReactNode;
  /** Number of rows in `children`; decides whether the expander is needed. */
  count: number;
  collapseAfter?: 2 | 3;
  /** One muted line when there is nothing — 13 §States: no teaching, no CTA. */
  emptyLabel: string;
  /** Rendered inside the card below the rows (e.g. the "Tải thêm" pager). */
  footer?: ReactNode;
}) {
  const { t } = useTranslation('students');
  const [expanded, setExpanded] = useState(false);
  const collapsible = collapseAfter !== undefined && count > collapseAfter;

  if (count === 0) {
    return <p className="py-[19.2px] text-center text-sm text-surface-400">{emptyLabel}</p>;
  }

  return (
    <>
      <div className="rounded-md border border-surface-200 bg-surface-0 px-4">
        {/* Rows sit in their own wrapper so the nth-child collapse selector can
            never reach `footer` (it would otherwise count footer as one more
            positional child and could hide it), and so the actual last ROW —
            not whatever `footer` happens to be — is what `last:border-b-0`
            sees as `:last-child`. */}
        <div
          className={cn(
            // Hide every row past the cut-off on phones until expanded. Scoped to
            // this list so a nested one can never be caught by it.
            collapsible && !expanded && COLLAPSE_CLASS[collapseAfter!],
          )}
        >
          {children}
        </div>
        {footer}
      </div>
      {collapsible && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 inline-flex min-h-[38.4px] items-center gap-[3.2px] text-[13px] font-medium text-brand-700 md:hidden"
        >
          {t('detail.activity.viewAll')}
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      )}
    </>
  );
}
